import React, { useState } from "react";
import PropTypes from "prop-types";

import Grid from "@material-ui/core/Grid";
import CustomSelect from "components/CustomSelect";
import CustomCheckBox from "components/CheckBox";
import CustomPhoneNumber from "components/CustomPhoneNumber";
import Button from "@material-ui/core/Button";
import { hashCode } from "utils";
import { useTranslation } from "react-i18next";
import ConfirmDialog from "@Components/ConfirmDialog";

import { useNotifyContext } from "components/Notify";
import eightAxios from "configs/axiosConfig";

const contactModes = [
  { id: "Call", name: "CALL" },
  { id: "Whatsapp", name: "WHATSAPP" },
];

const ContactDetailContainer = (props) => {
  const { t } = useTranslation();
  const { data, classes, contacts, setContacts, renderOnly } = props;
  const [number, setNumber] = useState(
    data.contact_number ? data.contact_number : ""
  );
  const [contactMode, setContactMode] = useState(data.mode ? data.mode : "");
  const [active, setActive] = useState(data.active ? data.active : false);
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: "",
    subTitle: "",
  });
  const setNotify = useNotifyContext();

  const hashes = contacts.map((contact) =>
    hashCode(contact.contact_number + contact.mode)
  );

  const confirmContactAppend = (item) => {
    const payload = {
      ...item,
      contact_number: number,
      mode: contactMode,
      active: active,
      confirmed: true,
      editMode: false,
    };

    if (!number) {
      setNotify({
        isOpen: true,
        message: "Cannot add contact with empty phone number",
        type: "error",
      });
      return;
    }
    if (!contactMode) {
      setNotify({
        isOpen: true,
        message: "Cannot add contact with empty contact mode",
        type: "error",
      });
      return;
    }

    const hash = hashCode(number + contactMode);
    if (hashes.includes(hash) && !data.editMode) {
      setNotify({
        isOpen: true,
        message: "Number with this contact mode already exists",
        type: "error",
      });
      return;
    }
    if (data.editMode) {
      const editedContactsLit = contacts.map((contact) => {
        if (contact !== item) {
          return contact;
        }
        return payload;
      });
      setContacts(editedContactsLit);

      setNotify({
        isOpen: true,
        message: "Contact Edited",
        type: "success",
      });

      return;
    }

    const modifiedContactsState = contacts.map((contact) => {
      if (contact.confirmed) {
        return contact;
      }
      return payload;
    });
    setContacts(modifiedContactsState);

    setNotify({
      isOpen: true,
      message: "Contact Added",
      type: "success",
    });
  };

  const editContactAndAppend = (item) => {
    const editableContactsList = contacts.map((contact) => {
      if (contact !== item) {
        return contact;
      }
      return { ...contact, confirmed: false, editMode: true };
    });
    setContacts(editableContactsList);
  };

  const onDelete = (item) => {
    if ("id" in item) {
      eightAxios
        .delete(`shop/delete-contact/${item.id}`)
        .then((response) => {
          const contactsAfterDeletion = contacts.filter(
            (contact) => contact !== item
          );
          setContacts(contactsAfterDeletion);
          setNotify({
            isOpen: true,
            message: response.data.message,
            type: "success",
          });
        })
        .catch((error) => {
          console.log(error);
          setNotify({
            isOpen: true,
            message: error.response.data.message,
            type: "error",
          });
        });
    } else {
      const contactsAfterDeletion = contacts.filter(
        (contact) => contact !== item
      );
      setContacts(contactsAfterDeletion);
      setNotify({
        isOpen: true,
        message: "Contact deleted successfully",
        type: "success",
      });
    }
  };

  const removeFromList = (item) => {

    if (!item.contact_number && !item.mode) {
      onDelete(item);
      return;
    }
  
    setConfirmDialog({
      isOpen: true,
      title: "Are you sure to delete this record?",
      subTitle: "YOU CANNOT UNDO THIS OPERATION",
      onConfirm: () => {
        onDelete(item);
      },
    });
  };

  return renderOnly ? (
    <>
      <Grid
        item
        container
        direction="row"
        spacing={2}
        xs={12}
        className={classes.tableStyle}
      >
        <Grid item lg={4}>
          <CustomPhoneNumber
            value={number}
            setValue={setNumber}
            classes={classes.inputStyle}
            disabled={data.confirmed}
            defaultCountry={"my"}
            onlyCountries={["my"]}
          />
        </Grid>
        <Grid item lg={4}>
          <CustomSelect
            options={contactModes}
            value={contactMode}
            onChange={(event) => setContactMode(event.target.value)}
            disabled={data.confirmed}
          />
        </Grid>
        <Grid item lg={4}>
          <CustomCheckBox
            value={active}
            checked={active}
            onChange={(event) => setActive(event.target.checked)}
            disabled={data.confirmed}
          />
        </Grid>
      </Grid>

      <ConfirmDialog
        confirmDialog={confirmDialog}
        setConfirmDialog={setConfirmDialog}
      />
    </>
  ) : (
    <>
      <Grid
        item
        container
        direction="row"
        spacing={2}
        xs={12}
        className={classes.tableStyle}
      >
        <Grid item lg={3}>
          <CustomPhoneNumber
            value={number}
            setValue={setNumber}
            classes={classes.inputStyle}
            disabled={data.confirmed}
            defaultCountry={"my"}
            onlyCountries={["my"]}
          />
        </Grid>
        <Grid item lg={3}>
          <CustomSelect
            options={contactModes}
            value={contactMode}
            onChange={(event) => setContactMode(event.target.value)}
            disabled={data.confirmed}
          />
        </Grid>
        <Grid item lg={3}>
          <CustomCheckBox
            value={active}
            checked={active}
            onChange={(event) => setActive(event.target.checked)}
            disabled={data.confirmed}
          />
        </Grid>
        <Grid item lg={3} className={classes.actionColumn}>
          {data.confirmed ? (
            <Button
              variant="contained"
              className={classes.button}
              onClick={() => editContactAndAppend(data)}
            >
              {t("Edit")}
            </Button>
          ) : (
            <Button
              variant="contained"
              color="primary"
              className={classes.button}
              onClick={() => confirmContactAppend(data)}
            >
              {t("Confirm")}
            </Button>
          )}

          <Button
            variant="contained"
            className={classes.button}
            onClick={() => removeFromList(data)}
          >
            {t("Delete")}
          </Button>
        </Grid>
      </Grid>

      <ConfirmDialog
        confirmDialog={confirmDialog}
        setConfirmDialog={setConfirmDialog}
      />
    </>
  );
};
ContactDetailContainer.propTypes = {
  data: PropTypes.object,
  classes: PropTypes.object,
  contacts: PropTypes.array,
  setContacts: PropTypes.func,
  renderOnly: PropTypes.bool,
};

export default ContactDetailContainer;
